import { captureException, withScope } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import chunk from "lodash/chunk";
import compact from "lodash/compact";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useQueryCallbacks } from "@/hooks/useQueryCallbacks";
import { ownErrorHandlingMeta } from "@/hooks/useQueryClient";
import {
  getRoaming,
  getSimRoamingCallPackageUsage,
  useGetESimInfo,
  useGetSimCustomerActions,
} from "@/services/api";
import {
  ESimInfo,
  GetRoamingPackageUsageResponse,
  RoamingInfo,
  SimCardDetail,
  SimCustomerInfoCreditClass,
} from "@/services/model";
import { AlertTypes } from "@/utils/atoms";
import { shouldFetchCockpitRoamingForSim } from "@/utils/cockpitRoamingFetch";
import { isUnscannedEsimInfo } from "@/utils/deviceUtils";
import { getSimIdsNeedingRoamingCallPackageUsage } from "@/utils/productMatrix";

import { useAlert } from "./useAlert";
import { useLocalStorageFlag } from "./useLocalStorageFlag";

/** Backend `/sim/roamingInfo` rejects requests with more than this many SIM IDs. */
export const MAX_ROAMING_INFO_SIM_IDS = 50;

/** Fetches roaming info in batches so bulk cockpit loads stay within the API limit. */
export const fetchRoamingInfoInBatches = async (
  simIds: number[],
): Promise<RoamingInfo[]> => {
  if (simIds.length === 0) {
    return [];
  }

  const batches = await Promise.all(
    chunk(simIds, MAX_ROAMING_INFO_SIM_IDS).map((batch) =>
      getRoaming({ simIds: batch }),
    ),
  );

  return batches.flat();
};

interface ExtendedSimData {
  roamingInfo?: RoamingInfo[];
  esimInfo?: ESimInfo;
  roamingCallPackageUsage?: GetRoamingPackageUsageResponse;
}

export interface ExtendedSimCardDetail extends SimCardDetail {
  roamingInfo?: RoamingInfo[];
  esimInfo?: ESimInfo;
  roamingCallPackageUsage?: GetRoamingPackageUsageResponse;
  creditClass?: SimCustomerInfoCreditClass | string | null;
}

export interface ExtendedSimCardDetailWithEsimInfo extends ExtendedSimCardDetail {
  matchingId: string;
  esimInfo: ESimInfo;
}

/**
 * This hook takes a list of sims and extends these by fetching esim and roaming info to result in a list of ExtendedSims
 * @param sims list of basic sims to extend
 * @param refetchInterval an optional interval for the api requests to refetch all data
 */
export const useExtendedSims = (
  sims: SimCardDetail[],
  refetchInterval?: false | number,
  needsIdCheck?: boolean,
) => {
  const showAlert = useAlert();
  const { t } = useTranslation();
  const {
    shouldShowError: shouldShowRoamingError,
    setErrorFired: setRoamingErrorFired,
  } = useLocalStorageFlag("roamingErrorFiredAt");
  const {
    shouldShowError: shouldShowEsimError,
    setErrorFired: setEsimErrorFired,
  } = useLocalStorageFlag("esimErrorFiredAt");

  const [extendedSimData, setExtendedSimData] = useState<{
    [key: string]: ExtendedSimData;
  }>({});
  const [isLoadingCallPackage, setIsLoadingCallPackage] = useState(false);

  // Account-level credit class (needed to skip international-call usage fetches for class C).
  const { data: simCustomerActions } = useGetSimCustomerActions();
  const creditClass = simCustomerActions?.creditClass;

  // Bulk roaming enrichment only for SIMs whose cockpit tiles show usage.
  const roamingSimIds = useMemo(
    () =>
      compact(
        sims
          .filter((sim) => shouldFetchCockpitRoamingForSim(sim, needsIdCheck))
          .map((sim) => sim.simId),
      ),
    [needsIdCheck, sims],
  );

  const roamingQueryKey = ["/sim/roamingInfo", ...roamingSimIds];
  const roamingQuery = useQuery({
    // Spread ids so react-query treats list identity like the generated hook.
    queryKey: roamingQueryKey,
    enabled: roamingSimIds.length > 0,
    refetchInterval,
    refetchOnMount: true,
    queryFn: () => fetchRoamingInfoInBatches(roamingSimIds),
    meta: ownErrorHandlingMeta,
  });
  useQueryCallbacks(
    { ...roamingQuery, queryKey: roamingQueryKey },
    {
      onSuccess: (roamingInfos) => {
        const newExtendedSimData = {
          ...extendedSimData,
        };

        roamingSimIds.forEach((simCardID) => {
          const simKey = String(simCardID);
          newExtendedSimData[simKey] = {
            ...(newExtendedSimData[simKey] ?? {}),
            // API volumes are already MB — same unit as the roaming tab.
            roamingInfo:
              roamingInfos?.filter(({ simId }) => simId === simCardID) ?? [],
          };
        });
        setExtendedSimData(newExtendedSimData);
      },
      onError: () => {
        if (shouldShowRoamingError()) {
          showAlert({
            type: AlertTypes.info,
            text: t("Error.roamingError"),
          });
          setRoamingErrorFired();
        }
      },
    },
  );
  const { isFetching: isLoadingRoaming } = roamingQuery;

  // Finite included international-call allowances (e.g. Flat Mobile) need a usage fetch.
  const callPackageSimIds = useMemo(
    () =>
      getSimIdsNeedingRoamingCallPackageUsage(sims, creditClass, needsIdCheck),
    [needsIdCheck, sims, creditClass],
  );
  const callPackageSimIdsKey = callPackageSimIds.join(",");

  useEffect(() => {
    if (callPackageSimIds.length === 0) {
      setIsLoadingCallPackage(false);

      return;
    }

    let cancelled = false;
    setIsLoadingCallPackage(true);

    Promise.allSettled(
      callPackageSimIds.map((simId) =>
        getSimRoamingCallPackageUsage({ simId }).then((data) => ({
          simId,
          data,
        })),
      ),
    ).then((results) => {
      if (cancelled) {
        return;
      }

      const fulfilled = results.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : [],
      );

      results.forEach((result, index) => {
        if (result.status === "rejected") {
          // TODO: replace by better error handler
          // Redacted category only, no simId/raw reason (see Sentry capture below for diagnostics)
          console.error(
            "[useExtendedSims] roamingCallPackageUsage fetch failed",
          );
          withScope((scope) => {
            scope.setTag("section", "portal");
            scope.setTag("domain", "useExtendedSims");
            scope.setTag("endpoint", "roamingCallPackageUsage");
            scope.setExtra("simId", callPackageSimIds[index]);
            captureException(result.reason);
          });
        }
      });

      setExtendedSimData((prev) => {
        const next = { ...prev };
        fulfilled.forEach(({ simId, data }) => {
          const simKey = String(simId);
          next[simKey] = {
            ...(next[simKey] ?? {}),
            roamingCallPackageUsage: data,
          };
        });

        return next;
      });

      setIsLoadingCallPackage(false);
    });

    return () => {
      cancelled = true;
    };
    // callPackageSimIdsKey keeps the dependency stable across identical id lists
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callPackageSimIdsKey]);

  // get a list of SimIDs where esim infos are missing
  // if `remainingDays` is not set, the SIM is not ready for further processing, so we don't need additional data for now
  const esims = compact(
    sims.filter(
      (sim) =>
        sim.simId !== undefined &&
        sim.remainingDays !== undefined &&
        sim.simType === "eSIM",
    ),
  );
  const missingEsimsIDs = useMemo(
    () => compact(esims.map((sim) => sim.simId)),
    [esims],
  );

  const eSimInfoQuery = useGetESimInfo(
    { simIds: missingEsimsIDs },
    {
      query: {
        meta: ownErrorHandlingMeta,
        enabled: missingEsimsIDs.length > 0,
        refetchInterval,
        refetchOnMount: true,
      },
    },
  );
  useQueryCallbacks(eSimInfoQuery, {
    onSuccess: (esimInfos) => {
      const newExtendedSimData = {
        ...extendedSimData,
      };
      missingEsimsIDs.forEach((simCardID) => {
        const simKey = String(simCardID);
        newExtendedSimData[simKey] = {
          ...(newExtendedSimData[simKey] ?? {}),
          esimInfo: esimInfos?.find(({ simId }) => simId === simCardID) ?? {},
        };
      });
      setExtendedSimData(newExtendedSimData);
    },
    onError: () => {
      if (shouldShowEsimError()) {
        showAlert({
          type: AlertTypes.info,
          text: t("Error.eSimError"),
        });
        setEsimErrorFired();
      }
    },
  });
  const { isFetching: isLoadingEsim } = eSimInfoQuery;

  // enrich the given sims and make them extended sims
  const extendedSims: ExtendedSimCardDetail[] = sims.map((simCard) => ({
    ...simCard,
    ...(extendedSimData[String(simCard.simId)] ?? {}),
    creditClass,
  }));

  const isLoading = isLoadingRoaming || isLoadingEsim || isLoadingCallPackage;

  // determine whether there are unscanned E-SIMs
  const hasUnscannedEsims = extendedSims.some((simCard) =>
    simCard.esimInfo ? isUnscannedEsimInfo(simCard.esimInfo) : false,
  );

  return { extendedSims, isLoading, hasUnscannedEsims, creditClass };
};
